<template>
  <ValidationObserver
    ref="validationObserver"
    tag="div"
    class="ce-modalContent ce-requestSampleForm">
    <div
      v-if="previousRequest"
      class="ce-requestSampleForm-prevRequestHint mb4 ms-1">
      <fa-icon
        class="mr1"
        icon="exclamation-circle" />
      {{ $t('requestSampleForm.prevRequestHint', { date: previousRequestDate, time: previousRequestTime }) }}
    </div>

    <alert
      class="mb3"
      v-if="hasMessage"
      :message="message.content"
      :type="message.type"
      @close="message = {}" />

    <!-- Readonly user info -->
    <div>
      <div class="flex flex-wrap">
        <div class="ce-field col-12 xs-col-6 xs-pr3">
          <label>{{ $t('contactForm.firstName') }}</label>
          <input
            type="text"
            readonly
            :value="user.firstName">
        </div>
        <div class="ce-field col-12 xs-col-6 xs-pl3">
          <label>{{ $t('contactForm.lastName') }}</label>
          <input
            type="text"
            readonly
            :value="user.lastName">
        </div>
      </div>
      <div class="ce-field">
        <label>{{ $t('contactForm.email') }}</label>
        <input
          type="text"
          readonly
          :value="user.email">
      </div>
      <div class="ce-field">
        <label>{{ $t('contactForm.company') }}</label>
        <input
          type="text"
          readonly
          :value="user.company">
      </div>
    </div>

    <!-- (Sub-) Region selection -->
    <div>
      <ValidationProvider
        v-if="regionSelectionNecessary"
        :name="$t('contactForm.region')"
        v-slot="{ errors }"
        rules="required"
        mode="eager"
        tag="div"
        class="ce-field">
        <label>
          {{ $t('contactForm.region') }} <span>*</span>
          <span
            v-if="errors.length > 0"
            class="ms-2 ml1 regular ce-colorError">
            {{ errors[0] }}
          </span>
        </label>
        <select
          v-model="form.region">
          <option
            v-for="region in contactRegions"
            :key="region.code"
            :value="region.code">
            {{ region.title }}
          </option>
        </select>
      </ValidationProvider>

      <ValidationProvider
        v-if="regionSelectionNecessary && selectedRegion && selectedRegion.subRegions"
        :name="$t('contactForm.subRegion')"
        v-slot="{ errors }"
        rules="required"
        mode="eager"
        tag="div"
        class="ce-field">
        <label>
          {{ $t('contactForm.subRegion') }} <span>*</span>
          <span
            v-if="errors.length > 0"
            class="ms-2 ml1 regular ce-colorError">
            {{ errors[0] }}
          </span>
        </label>
        <select
          v-model="form.subRegion">
          <option
            v-for="subRegion in selectedRegion.subRegions"
            :key="subRegion.code"
            :value="subRegion.code">
            {{ subRegion.title }}
          </option>
        </select>
      </ValidationProvider>
    </div>

    <address-form
      :label="$t('requestSampleForm.customerAddress')"
      v-model="form.address.customer" />

    <div class="ce-field">
      <checkbox
        id="customShippingAddress"
        v-model="form.address.customShippingAddress">
        {{ $t('requestSampleForm.customShippingAddress') }}
      </checkbox>
    </div>

    <address-form
      v-if="form.address.customShippingAddress"
      :label="$t('requestSampleForm.shippingAddress')"
      v-model="form.address.shipping" />

    <validated-input
      name="reason"
      :value="form.reason"
      @input="onInput"
      type="textarea"
      :label="$t('requestSampleForm.reason')"
      rules="required" />

    <div
      v-if="consentHintNecessary"
      class="ms-1 line-height-4">
      <i18n
        path="contactForm.consentHint"
        tag="p">
        <a
          :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
          rel="noopener noreferrer">
          {{ $t('contactForm.dataProtectionPage') }}</a>
      </i18n>
    </div>

    <div class="mt4">
      <button
        class="ce-btn ce-btn-light ce-btn--fullWidth"
        @click.prevent="submit">
        <template v-if="isSending">
          <fa-icon
            icon="spinner"
            spin />
        </template>
        <template v-else>
          {{ $t('requestSampleForm.submitCta') }}
        </template>
      </button>
    </div>
  </ValidationObserver>
</template>

<script>
import { parseISO, format } from 'date-fns';
import UserForm from 'mixins/UserForm';
import Checkbox from 'components/Checkbox.vue';
import AddressForm from 'components/forms/AddressForm.vue';
import axios from 'axios';

export default {
  data() {
    return {
      form: {
        address: {
          customer: {},
          customShippingAddress: false,
          shipping: {}
        },
        reason: null
      },
      previousRequest: null
    };
  },
  components: { Checkbox, AddressForm },
  mixins: [UserForm],
  computed: {
    regionSelectionNecessary() {
      return !(this.user && this.isValidated);
    },
    eventLabel() {
      return {
        prd: this.additionalData.prd,
        productName: this.additionalData.product_name,
        selectedIndustry: this.additionalData.selected_industry ? this.additionalData.selected_industry : 'unknown'
      };
    },
    previousRequestDate() {
      return this.previousRequest
        ? format(parseISO(this.previousRequest.createdDate), 'yyyy/MM/dd')
        : null;
    },
    previousRequestTime() {
      return this.previousRequest
        ? format(parseISO(this.previousRequest.createdDate), 'HH:mm')
        : null;
    }
  },
  created() {
    this.checkForPreviousRequest();
  },
  methods: {
    async checkForPreviousRequest() {
      this.previousRequest = await this.$store.dispatch('user/getSampleRequestForPRD', this.additionalData.prd);
    },
    reset() {
      this.isSending = false;
      this.form = {
        address: {
          customer: {},
          customShippingAddress: false,
          shipping: {}
        },
        reason: null
      };
      if (this.$refs.validationObserver) {
        this.$refs.validationObserver.reset();
      }
    },
    async submit() {
      const validationResult = await this.$refs.validationObserver.validate();
      if (!validationResult) return;
      this.isSending = true;

      const data = {
        ...this.additionalData,
        formSecurityToken: this.formSecurityToken,
        reason: this.form.reason,
        address: this.form.address,
        recipient: this.isValidated ? [this.user.customerService.email, this.user.accountManager.email] : null
      };

      ['firstName', 'lastName', 'company', 'email', 'region', 'subRegion'].forEach((f) => {
        data[f] = this.user && this.user[f] ? this.user[f] : this.form[f];
      });

      try {
        await axios.post(`${location.origin}/api/v1/message/requestSampleForm`, data);
        this.$emit('finished');

        this.$trackEvent(
          'Form',
          'Request a sample',
          this.eventLabel
        );

        this.reset();
      } catch (e) {
        this.message = {
          type: 'error',
          content: e.response?.data?.message ?? e.message
        };
      }

      this.isSending = false;
    }
  }
};
</script>

<style lang="less">
  @import (reference) "~styles/includes/base";

  .ce-requestSampleForm {
    .ce-requestSampleForm-prevRequestHint {
      color: @color-warn;
    }
  }
</style>
